/**
 * URL handling for the links the backend hands us.
 *
 * Backend domains arrive in mixed shapes: bare (`123j88.com`), with a path
 * (`http://fc.j88daily.com/Account/Login`), or protocol-relative (`//x.com`).
 * The rules here are a straight port of Android's `core/util/UrlUtils.kt`, so a
 * link that resolves one way there resolves the same way here.
 */

/**
 * Gives `raw` an http(s) scheme so it can be requested directly.
 *
 * A bare domain defaults to `https://` — the same default Android applies, which
 * matters: if this app defaulted to `http://` instead, every bare domain in the
 * list would be blocked as mixed content (see `isPlainHttp`) and report
 * `unhealthy` while Android reported it healthy. Two apps, one list, opposite
 * answers, and nothing in the data to say why.
 */
export function ensureScheme(raw: string): string {
  const url = raw.trim();
  if (url === '') return url;
  const lower = url.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return `https://${url}`;
}

/**
 * Whether this URL is plain `http://` and therefore **cannot be probed at all**
 * from an `https` page (SPEC §7.3, §14 #3).
 *
 * Checked *before* `fetch` is ever called, and reported with its own error
 * string. Letting it fall into the generic network `catch` would mix "died
 * because of the network" — variable, and the thing we are here to measure —
 * with "died because the browser refuses on policy" — constant, and known in
 * advance. Pooled together they are indistinguishable in the submitted data.
 */
export function isPlainHttp(url: string): boolean {
  return url.toLowerCase().startsWith('http://');
}

/**
 * Whether a URL is well-formed enough to hand to `fetch`.
 *
 * `fetch` throws a `TypeError` synchronously on a malformed URL, which would be
 * caught by the probe's catch-all and reported as a network failure — a link
 * that is simply mistyped in the backend list would then look like a link the
 * carrier is blocking.
 */
export function isParsableUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname !== '';
  } catch {
    return false;
  }
}

/** Host only, for display. Never throws — falls back to the raw string. */
export function hostOf(url: string): string {
  try {
    return new URL(ensureScheme(url)).host || url;
  } catch {
    return url;
  }
}
